import Vue from 'vue'
import Router from 'vue-router'
import ShortKey from 'vue-shortkey'

import axios from 'axios'
import VueAxios from 'vue-axios'
import VueCookie from 'vue-cookie'
import * as moment from 'moment'

// this will make Moment automatically available across any and all components (this.$moment)
Object.defineProperty(Vue.prototype, '$moment', { value: moment })

import store from '../store'

import constants from '../config/constants'
import interceptor from '../helpers/interceptor'
import { getSession, removeSession } from '../helpers/session'
import mixins from '../mixins'

const AUTH_HEADER = constants.auth_header_key
const REFRESH_TOKEN = constants.refresh_token_key

let axios_instance = axios.create({
  baseURL: process.env.VUE_APP_API_ROOT
})

axios_instance.defaults.headers['Access-Control-Allow-Origin'] = "*"

let sessionId = getSession("sessionId")
if (sessionId) {
  axios_instance.defaults.headers.common[AUTH_HEADER] = "Bearer " + sessionId
}

let refreshToken = getSession("refreshToken")
if (refreshToken) {
  axios_instance.defaults.headers.common[REFRESH_TOKEN] = refreshToken
}

Vue.use(Router)
Vue.use(ShortKey)
Vue.use(VueAxios, axios_instance)
Vue.use(VueCookie)

Vue.mixin(mixins)

const router = new Router({
  mode: 'hash',
  base: __dirname,
  routes: [
    {
      path: '/',
      name: 'Home',
      redirect: '/dashboard',
      component: () => import('../pages/Home'),
      children: [
        {
          path: 'dashboard',
          name: 'Dashboard',
          component: () => import('../pages/Dashboard')
        },
        {
          path: 'profile',
          name: 'Profile',
          component: () => import('../pages/Profile')
        },
        {
          path: 'change-password',
          name: 'ChangePassword',
          component: () => import('../pages/ChangePassword')
        },
        {
          path: 'modules',
          name: 'Module',
          component: () => import('../pages/Modules'),
          beforeEnter: hasModuleAccess,
          meta: { module: 'module' }
        },
        {
          path: 'module-accesses',
          name: 'ModuleAccess',
          component: () => import('../pages/ModuleAccesses'),
          beforeEnter: hasModuleAccess,
          meta: { module: 'roleaccess' }
        },
        {
          path: 'roles',
          name: 'Role',
          component: () => import('../pages/Roles'),
          beforeEnter: hasModuleAccess,
          meta: { module: 'role' }
        },
        {
          path: 'users',
          name: 'User',
          component: () => import('../pages/Users'),
          beforeEnter: hasModuleAccess,
          meta: { module: 'user' }
        },
        {
          path: 'countries',
          name: 'Country',
          component: () => import('../pages/Countries'),
          beforeEnter: hasModuleAccess,
          meta: { module: 'country' }
        },
        {
          path: 'states',
          name: 'State',
          component: () => import('../pages/States'),
          beforeEnter: hasModuleAccess,
          meta: { module: 'state' }
        },
        {
          path: 'districts',
          name: 'District',
          component: () => import('../pages/Districts'),
          beforeEnter: hasModuleAccess,
          meta: { module: 'district' }
        },
        {
          path: 'cities',
          name: 'City',
          component: () => import('../pages/Cities'),
          beforeEnter: hasModuleAccess,
          meta: { module: 'city' }
        },
        {
          path: 'panchayaths',
          name: 'Panchayath',
          component: () => import('../pages/Panchayaths'),
          beforeEnter: hasModuleAccess,
          meta: { module: 'panchayath' }
        },
        // {
        //   path: 'media',
        //   name: 'Media',
        //   component: () => import('../pages/Media'),
        //   beforeEnter: hasModuleAccess,
        //   meta: { module: 'media' }
        // },
        {
          path: 'agents',
          name: 'Agent',
          component: () => import('../pages/Agents'),
          beforeEnter: hasModuleAccess,
          meta: { module: 'agent' }
        },
        {
          path: 'item-categories',
          name: 'ItemCategory',
          component: () => import('../pages/ItemCategories_2'),
          beforeEnter: hasModuleAccess,
          meta: { module: 'itemcategory' }
        },
        {
          path: 'items',
          name: 'Item',
          component: () => import('../pages/Items'),
          beforeEnter: hasModuleAccess,
          meta: { module: 'item' }
        },
        {
          path: 'raw-materials',
          name: 'RawMaterial',
          component: () => import('../pages/RawMaterials'),
          beforeEnter: hasModuleAccess,
          meta: { module: 'rawmaterial' }
        },
        {
          path: 'customer-types',
          name: 'CustomerType',
          component: () => import('../pages/CustomerTypes'),
          beforeEnter: hasModuleAccess,
          meta: { module: 'customertype' }
        },
        {
          path: 'customers',
          name: 'Customer',
          component: () => import('../pages/Customers'),
          beforeEnter: hasModuleAccess,
          meta: { module: 'customer' }
        },
        {
          path: 'units',
          name: 'Unit',
          component: () => import('../pages/Units'),
          beforeEnter: hasModuleAccess,
          meta: { module: 'unit' }
        },
        {
          path: 'taxes',
          name: 'Tax',
          component: () => import('../pages/Taxes'),
          beforeEnter: hasModuleAccess,
          meta: { module: 'tax' }
        },
        {
          path: 'suppliers',
          name: 'Distributor',
          component: () => import('../pages/Distributors'),
          beforeEnter: hasModuleAccess,
          meta: { module: 'distributor' }
        },
        {
          path: 'branches',
          name: 'Retailer',
          component: () => import('../pages/Retailers'),
          beforeEnter: hasModuleAccess,
          meta: { module: 'retailer' }
        },
        {
          path: 'proforma-invoice',
          name: 'ProformaInvoice',
          component: () => import('../pages/Invoice'),
          beforeEnter: hasModuleAccess,
          meta: { module: 'invoice' }
        },
        {
          path: 'proforma-invoice/:invoiceCode',
          name: 'ProformaInvoiceEdit',
          component: () => import('../pages/Invoice'),
          beforeEnter: hasModuleAccess,
          meta: { module: 'invoice' }
        },
        {
          path: 'proforma-invoice/:invoiceCode/approve',
          name: 'ProformaInvoiceApprove',
          component: () => import('../pages/Invoice'),
          beforeEnter: hasModuleAccess,
          meta: { module: 'invoice' },
          props: (route) => (
          {
            approveMode: true
          })
        },
        {
          path: 'proforma-invoice/:invoiceCode/view',
          name: 'ProformaInvoiceView',
          component: () => import('../pages/Invoice'),
          beforeEnter: hasModuleAccess,
          meta: { module: 'invoice' },
          props: (route) => (
          {
            viewMode: true
          })
        },
        {
          path: 'proforma-invoice-list',
          name: 'ProformaInvoiceList',
          component: () => import('../pages/InvoiceList'),
          beforeEnter: hasModuleAccess,
          meta: { module: 'invoice' }
        },
        {
          path: 'sales-list',
          name: 'SalesList',
          component: () => import('../pages/SalesList'),
          beforeEnter: hasModuleAccess,
          meta: { module: 'sale' }
        },
        {
          path: 'sales-return',
          name: 'SalesReturn',
          component: () => import('../pages/SalesReturn'),
          beforeEnter: hasModuleAccess,
          meta: { module: 'salereturn' }
        },
        {
          path: 'sales-return/:saleReturnId',
          name: 'EditSalesReturn',
          component: () => import('../pages/SalesReturn'),
          beforeEnter: hasModuleAccess,
          meta: { module: 'salereturn' }
        },
        {
          path: 'sales-return/:saleReturnId/view',
          name: 'ViewSalesReturn',
          component: () => import('../pages/SalesReturn'),
          beforeEnter: hasModuleAccess,
          meta: { module: 'salereturn' },
          props: (route) => (
          {
            viewMode: true
          })
        },
        {
          path: 'sales-return-list',
          name: 'SalesReturnList',
          component: () => import('../pages/SalesReturnList'),
          beforeEnter: hasModuleAccess,
          meta: { module: 'salereturn' }
        },
        {
          path: 'sales-return/:saleReturnId/approve',
          name: 'ApproveSalesReturn',
          component: () => import('../pages/SalesReturn'),
          beforeEnter: hasModuleAccess,
          meta: { module: 'salereturn' },
          props: (route) => (
          {
            approveMode: true
          })
        },
        {
          path: 'purchase',
          name: 'Purchase',
          component: () => import('../pages/Purchase'),
          beforeEnter: hasModuleAccess,
          meta: { module: 'purchase' }
        },
        {
          path: 'purchase/:purchaseId',
          name: 'PurchaseEdit',
          component: () => import('../pages/Purchase'),
          beforeEnter: hasModuleAccess,
          meta: { module: 'purchase' }
        },
        {
          path: 'purchase/view/:purchaseId',
          name: 'PurchaseView',
          component: () => import('../pages/Purchase'),
          beforeEnter: hasModuleAccess,
          meta: { module: 'purchase' },
          props: (route) => (
          {
            viewMode: true
          })
        },
        {
          path: 'purchase-list',
          name: 'PurchaseList',
          component: () => import('../pages/PurchaseList'),
          beforeEnter: hasModuleAccess,
          meta: { module: 'purchase' }
        },
        {
          path: 'purchase-return',
          name: 'PurchaseReturn',
          component: () => import('../pages/PurchaseReturn'),
          beforeEnter: hasModuleAccess,
          meta: { module: 'purchasereturn' }
        },
        {
          path: 'purchase-return/:purchaseReturnId',
          name: 'PurchaseReturnEdit',
          component: () => import('../pages/PurchaseReturn'),
          beforeEnter: hasModuleAccess,
          meta: { module: 'purchasereturn' }
        },
        {
          path: 'purchase-return/:purchaseReturnId/view',
          name: 'PurchaseReturnView',
          component: () => import('../pages/PurchaseReturn'),
          beforeEnter: hasModuleAccess,
          meta: { module: 'purchasereturn' },
          props: (route) => (
          {
            viewMode: true
          })
        },
        {
          path: 'purchase-return/:purchaseReturnId/approve',
          name: 'PurchaseReturnApprove',
          component: () => import('../pages/PurchaseReturn'),
          beforeEnter: hasModuleAccess,
          meta: { module: 'purchasereturn' },
          props: (route) => (
          {
            approveMode: true
          })
        },
        {
          path: 'purchase-return-list',
          name: 'PurchaseReturnList',
          component: () => import('../pages/PurchaseReturnList'),
          beforeEnter: hasModuleAccess,
          meta: { module: 'purchasereturn' }
        },
        {
          path: 'production',
          name: 'Production',
          component: () => import('../pages/Production'),
          beforeEnter: hasModuleAccess,
          meta: { module: 'production' }
        },
        {
          path: 'production/:id',
          name: 'ProductionEdit',
          component: () => import('../pages/Production'),
          beforeEnter: hasModuleAccess,
          meta: { module: 'production' }
        },
        {
          path: 'production/view/:id',
          name: 'ProductionView',
          component: () => import('../pages/Production'),
          beforeEnter: hasModuleAccess,
          meta: { module: 'production' },
          props: (route) => (
          {
            viewMode: true
          })
        },
        {
          path: 'production-list',
          name: 'ProductionList',
          component: () => import('../pages/ProductionList'),
          beforeEnter: hasModuleAccess,
          meta: { module: 'production' }
        },
        {
          path: 'brands',
          name: 'Brand',
          component: () => import('../pages/Brands'),
          beforeEnter: hasModuleAccess,
          meta: { module: 'brand' }
        },
        {
          path: 'brand-accesses',
          name: 'BrandAccess',
          component: () => import('../pages/BrandAccesses'),
          beforeEnter: hasModuleAccess,
          meta: { module: 'brandaccess' }
        },
        {
          path: 'extra-discounts',
          name: 'ExtraDiscount',
          component: () => import('../pages/ExtraDiscounts'),
          beforeEnter: hasModuleAccess,
          meta: { module: 'extradiscount' }
        },
        {
          path: 'factories',
          name: 'Factory',
          component: () => import('../pages/Factories'),
          beforeEnter: hasModuleAccess,
          meta: { module: 'factory' }
        },
        {
          path: 'warehouses',
          name: 'Warehouse',
          component: () => import('../pages/Warehouses'),
          beforeEnter: hasModuleAccess,
          meta: { module: 'warehouse' }
        },
        {
          path: 'stock-dispatch',
          name: 'StockDispatch',
          component: () => import('../pages/StockDispatch'),
          beforeEnter: hasModuleAccess,
          meta: { module: 'factorydispatch' }
        },
        {
          path: 'stock-dispatch/:dispatchId',
          name: 'EditStockDispatch',
          component: () => import('../pages/StockDispatch'),
          beforeEnter: hasModuleAccess,
          meta: { module: 'factorydispatch' }
        },
        {
          path: 'stock-dispatch/:dispatchId/view',
          name: 'ViewStockDispatch',
          component: () => import('../pages/StockDispatch'),
          beforeEnter: hasModuleAccess,
          meta: { module: 'factorydispatch' },
          props: (route) => (
          {
            viewMode: true
          })
        },
        {
          path: 'stock-dispatch-list',
          name: 'StockDispatchList',
          component: () => import('../pages/StockDispatchList'),
          beforeEnter: hasModuleAccess,
          meta: { module: 'factorydispatch' }
        },
        {
          path: 'stock-movement',
          name: 'StockMovement',
          component: () => import('../pages/StockMovement'),
          beforeEnter: hasModuleAccess,
          meta: { module: 'stockmovement' }
        },
        {
          path: 'stock-movement/:id',
          name: 'EditStockMovement',
          component: () => import('../pages/StockMovement'),
          beforeEnter: hasModuleAccess,
          meta: { module: 'stockmovement' }
        },
        {
          path: 'stock-movement/:id/view',
          name: 'ViewStockMovement',
          component: () => import('../pages/StockMovement'),
          beforeEnter: hasModuleAccess,
          meta: { module: 'stockmovement' },
          props: (route) => (
          {
            viewMode: true
          })
        },
        {
          path: 'stock-movement-list',
          name: 'StockMovementList',
          component: () => import('../pages/StockMovementList'),
          beforeEnter: hasModuleAccess,
          meta: { module: 'stockmovement' }
        },
        {
          path: 'commission-list',
          name: 'CommissionList',
          component: () => import('../pages/CommissionList'),
          beforeEnter: hasModuleAccess,
          meta: { module: 'commission' }
        },
        {
          path: 'reports/rawmaterial-stock',
          name: 'RawmaterialStockReport',
          component: () => import('../pages/reports/RawmaterialStockReport'),
          beforeEnter: hasModuleAccess,
          meta: { module: 'rawmaterialstockreport' }
        },
        {
          path: 'reports/factory-stock',
          name: 'FactoryStockReport',
          component: () => import('../pages/reports/FactoryStockReport'),
          beforeEnter: hasModuleAccess,
          meta: { module: 'factorystockreport' }
        },
        {
          path: 'reports/warehouse-stock',
          name: 'WarehouseStockReport',
          component: () => import('../pages/reports/WarehouseStockReport'),
          beforeEnter: hasModuleAccess,
          meta: { module: 'warehousestockreport' }
        },
        {
          path: 'reports/branch-stock',
          name: 'RetailerStockReport',
          component: () => import('../pages/reports/RetailerStockReport'),
          beforeEnter: hasModuleAccess,
          meta: { module: 'retailerstockreport' }
        },
        {
          path: 'reports/sales',
          name: 'SalesReport',
          component: () => import('../pages/reports/SalesReport'),
          beforeEnter: hasModuleAccess,
          meta: { module: 'salesreport' }
        },
        {
          path: 'reports/sales-item',
          name: 'ItemSalesReport',
          component: () => import('../pages/reports/ItemSalesReport'),
          beforeEnter: hasModuleAccess,
          meta: { module: 'itemreport' }
        },
        {
          path: 'reports/purchase',
          name: 'PurchaseReport',
          component: () => import('../pages/reports/PurchaseReport'),
          beforeEnter: hasModuleAccess,
          meta: { module: 'purchasereport' }
        },
        {
          path: 'reports/purchase-item',
          name: 'ItemPurchaseReport',
          component: () => import('../pages/reports/ItemPurchaseReport'),
          beforeEnter: hasModuleAccess,
          meta: { module: 'purchaseitemreport' }
        },
        {
          path: 'reports/sales-return',
          name: 'SalesReturnReport',
          component: () => import('../pages/reports/SalesReturnReport'),
          beforeEnter: hasModuleAccess,
          meta: { module: 'salesreturnreport' }
        },
        {
          path: 'reports/purchase-return',
          name: 'PurchaseReturnReport',
          component: () => import('../pages/reports/PurchaseReturnReport'),
          beforeEnter: hasModuleAccess,
          meta: { module: 'purchasereturnreport' }
        },
        {
          path: 'reports/production',
          name: 'ProductionReport',
          component: () => import('../pages/reports/ProductionReport'),
          beforeEnter: hasModuleAccess,
          meta: { module: 'productionreport' }
        },
        {
          path: 'reports/commission',
          name: 'CommissionReport',
          component: () => import('../pages/reports/CommissionReport'),
          beforeEnter: hasModuleAccess,
          meta: { module: 'commissionreport' }
        },
        {
          path: 'settings',
          name: 'Settings',
          component: () => import('../pages/Settings')
        }
      ],
      beforeEnter: (to, from, next) => {
        if (!isLoggedIn()) {
          return next({ path: '/login' })
        }
        // setTimeout( () => {
          Promise.all([Vue.axios.get("admin"), Vue.axios.get("option"), Vue.axios.get("module")])
            .then(([adminResponse, optionResponse, moduleResponse]) => {

              if (optionResponse.data.success) {
            
                let settings = {}
                
                optionResponse.data.data.forEach(item => {
                  settings[item.option_name] = item
                })
                
                store.commit('setAppSettings', settings)
              }

              if (moduleResponse.data.success) {

                let modules = {}
                            
                moduleResponse.data.data.forEach(item => {
                  if (item.is_active) {
                    let module = {
                      id: item.id,
                      title: item.label
                    }
                    modules[item.module] = module
                  }
                })
                
                store.commit('setModules', modules)

              }
              
              if (adminResponse.data.success) {
                store.commit('setUser', adminResponse.data.data)
              }
              else{
                removeSession('sessionId')
                removeSession('refreshToken')
                Vue.axios.defaults.headers.common[AUTH_HEADER] = undefined
                Vue.axios.defaults.headers.common[REFRESH_TOKEN] = undefined
                next({ path: '/login' })
              }

              next()

            })

        // }, 2000)
        
      }
    },
    {
      path: '/proforma-invoice/:invoiceCode/print',
      name: 'PrintInvoice',
      component: () => import('../pages/PrintInvoice'),
      beforeEnter: (to, from, next) => {
        next(isLoggedIn() ? undefined : { path: '/login' })
      },
      meta: { module: 'invoice' },
      props: (route) => (
      {
        showCode: false,
        showTax: false
      })
    },
    {
      path: '/proforma-invoice/:invoiceCode/w-print',
      name: 'WarehousePrint',
      component: () => import('../pages/PrintInvoice'),
      beforeEnter: (to, from, next) => {
        next(isLoggedIn() ? undefined : { path: '/login' })
      },
      meta: { module: 'invoice' },
      props: (route) => (
      {
        showPrice: false
      })
    },
    {
      path: '/purchase/print/:id',
      name: 'PrintPurchase',
      component: () => import('../pages/PrintPurchase'),
      beforeEnter: (to, from, next) => {
        next(isLoggedIn() ? undefined : { path: '/login' })
      },
      meta: { module: 'purchase' }
    },
    {
      path: '/stock-dispatch/:id/print',
      name: 'PrintStockDispatch',
      component: () => import('../pages/PrintDispatch'),
      beforeEnter: (to, from, next) => {
        next(isLoggedIn() ? undefined : { path: '/login' })
      },
      meta: { module: 'factorydispatch' }
    },
    {
      path: '/login',
      name: 'Login',
      component: () => import('../pages/Login'),
      beforeEnter: (to, from, next) => {
        next(isLoggedIn() ? { path: '/' } : undefined)
      }
    },
    {
      path: '/forgot-password',
      name: 'PasswordForgot',
      component: () => import('../pages/PasswordForgot'),
      beforeEnter: (to, from, next) => {
        next(isLoggedIn() ? { path: '/' } : undefined)
      }
    },
    {
      path: '/reset-password/:token',
      name: 'PasswordReset',
      component: () => import('../pages/PasswordReset'),
      beforeEnter: (to, from, next) => {
        next(isLoggedIn() ? { path: '/' } : undefined)
      }
    }
  ]
})

router.beforeEach((to, from, next) => {
  // redirect 404 into login page
  next(!to.matched.length ? '/login' : undefined)
})

function hasModuleAccess (to, from, next) {
  // console.log("module => ", to.meta.module)
  next(interceptor.hasModuleAccess(to.meta.module) ? undefined : '/dashboard')
}

function isLoggedIn () {
  return !!Vue.axios.defaults.headers.common[AUTH_HEADER]
}

export default router
